import { useEffect, useMemo, useRef, useState } from "react";

import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { navigateForScan } from "../../contexts/ScannerContext";
import useScanToBasket from "../../hooks/useScanToBasket";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import BasketTable from "../../components/basket/BasketTable";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import FullscreenLoading from "../../components/shared/LoadingWheelFullscreen";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useBaskets from "../../data/useBaskets";
import classNames from "../../utils/classNames";
import PriceFormat from "../../utils/priceFormat";
import { IAccount } from "../../models/account/IAccount";
import useNotification from "../../components/notifications/useNotifications";
import useShippingPolicies from "../../data/useShippingPolicies";
import { IShippingPolicy } from "../../models/ShippingPolicy";
import BasketInvoiceInputs from "./BasketInvoiceInputs";
import { FormatType } from "../../models/EbayOffer";
import { DiscountType, IBasketItem } from "../../models/Basket";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import DiscountTypeToggleInput from "../../components/shared/Inputs/DiscountTypeToggleInput";
import SaveAsQuoteModal from "../../components/basket/SaveAsQuoteModal";
import { ICreateSalesQuoteRequest } from "../../models/SalesQuote";


export default function Basket() {
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { register, setValue, getValues, watch, reset } = useForm({
    defaultValues: {
      paymentMethod: 1,
      accountId: 0,
      shippingPolicyId: "0",
      notes: "",
      // PickAddress registers these at the top level of the form
      postcode: "",
      line1: "",
      line2: "",
      line3: "",
      line4: "",
      locality: "",
      town: "",
      county: "",
      addTaxToTotal: false,
      discountType: DiscountType.Flat,
      discountValue: "0",
    }
  });
  const { getBasket, clearBasket, checkout } = useBaskets("Basket");
  const hasTill = useFeatureFlag("Till");
  const { shippingPolicies: allShippingPolicies } = useShippingPolicies();
  const navigate = useNavigate();
  const [selectedAccount, setSelectedAccount] = useState<IAccount | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  // const [shippingPolicies, setShippingPolicies] = useState<Record<string, number | string>[]>([]);

  const addTaxToTotal = watch("addTaxToTotal");
  const discountType = watch("discountType");
  const discountValue = Math.max(0, Number(watch("discountValue")) || 0);

  const getItemPrice = (item: IBasketItem) => {
    // Use basket price if set, otherwise calculate default price
    if (item.basketPrice !== undefined && item.basketPrice !== null) {
      return Number(item.basketPrice) || 0;
    }

    // Fall back to calculating default price
    if (item.part.offers && item.part.offers.length > 0) {
      const offer = item.part.offers[0];
      if (offer.format === FormatType.FixedPrice) {
        return parseFloat(offer.buyItNowPrice?.toString() || "0") || 0;
      } else {
        return parseFloat(offer.auctionStartPrice?.toString() || "0") || 0;
      }
    }

    return parseFloat(item.part.enteredPrice?.toString() || "0") || 0;
  };

  // Don't need now it's a drawer - if we ever use this as a page too then we can add in conditionally
  // useEffect(() => {
  //   setInfo({
  //     name: "Basket",
  //     desc: "View and manage your current basket",
  //   });
  // }, [])

  useEffect(() => {
    if (!getBasket.isLoading && !allShippingPolicies.isLoading) {
      setIsLoading(false);
    }
  }, [
    getBasket.isLoading,
    getBasket.data,
    allShippingPolicies.isLoading,
    allShippingPolicies.data,
  ]);

  // While the basket is open, part scans add the part to the basket rather than
  // navigating to its stock page. Other scans fall back to the default routing.
  useScanToBasket({
    componentName: "Basket",
    onUnhandled: (scan) => navigateForScan(navigate, scan),
  });


  const loadedQuote = getBasket.data?.data.salesQuote ?? null;
  const hadQuoteRef = useRef(false);

  // A basket loaded from a sales quote arrives with the quote's form snapshot; put it back on the
  // form so checkout picks up the same shipping, address, discount and account the customer was quoted.
  // Clearing that basket drops the link, so the form goes back to blank too.
  useEffect(() => {
    if (allShippingPolicies.isLoading) return;

    if (!loadedQuote) {
      if (hadQuoteRef.current) {
        hadQuoteRef.current = false;
        reset();
        setSelectedAccount(undefined);
      }
      return;
    }

    hadQuoteRef.current = true;
    reset({
      paymentMethod: loadedQuote.paymentMethod ?? 1,
      accountId: 0,
      shippingPolicyId: loadedQuote.shippingPolicyId ?? "0",
      notes: loadedQuote.notes ?? "",
      postcode: loadedQuote.postcode ?? "",
      line1: loadedQuote.line1 ?? "",
      line2: loadedQuote.line2 ?? "",
      line3: loadedQuote.line3 ?? "",
      line4: loadedQuote.line4 ?? "",
      locality: "",
      town: loadedQuote.town ?? "",
      county: loadedQuote.county ?? "",
      addTaxToTotal: loadedQuote.addTaxToTotal,
      discountType: loadedQuote.discountType === DiscountType.Percentage ? DiscountType.Percentage : DiscountType.Flat,
      discountValue: String(loadedQuote.discountValue ?? 0),
    }, { keepDefaultValues: true });
    setSelectedAccount(loadedQuote.account ?? undefined);
  }, [getBasket.data?.data.salesQuoteId, allShippingPolicies.isLoading]);

  // The checkout body doubles as the quote snapshot, so both actions read the form the same way.
  const buildFormBody = () => {
    const data = getValues();
    return {
      accountId: selectedAccount?.id,
      paymentMethod: data.paymentMethod,
      shippingPolicyId: data.shippingPolicyId && data.shippingPolicyId !== "0" ? data.shippingPolicyId : undefined,
      notes: data.notes,
      addTaxToTotal: data.addTaxToTotal,
      discountType: data.discountType === DiscountType.Percentage ? DiscountType.Percentage : DiscountType.Flat,
      discountValue,
      line1: data.line1,
      line2: data.line2,
      line3: data.line3,
      line4: data.line4,
      town: data.town,
      county: data.county,
      postcode: data.postcode,
    };
  };

  const buildQuoteBody = (): Omit<ICreateSalesQuoteRequest, "contactName" | "contactEmail" | "contactPhone"> => ({
    ...buildFormBody(),
    quotedTotal: finalTotal,
  });

  const handleSubmit = () => {

    if (!selectedAccount) {
      addNotification({
        primaryText: "No Account Selected",
        variant: "warn"
      });
      return;
    }

    const data = getValues();

    if (data.shippingPolicyId === undefined || data.shippingPolicyId === "0") {
      addNotification({
        primaryText: "No Shipping Policy Selected",
        variant: "warn"
      });
      return;
    }

    setIsSubmitLoading(true);

    const body = {
      ...buildFormBody(),
      accountId: selectedAccount.id,
      shippingPolicyId: data.shippingPolicyId,
    }

    checkout.mutate(body, {
      onSuccess: (file) => {
        // console.log("Invoice received:", file);
        const url = window.URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `invoice-${getBasket.data?.data.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        reset();
      },
      onSettled: () => {
        setIsSubmitLoading(false);
        setIsConfirmOpen(false);
      }
    });
  };

  const isEmpty = useMemo(() => {
    return getBasket.data?.data.items && getBasket.data?.data.items.length === 0;
  }, [getBasket.data?.data.items]);

  const getShippingPrice = (shippingPolicyId: string) => {
    const policy = allShippingPolicies.data?.data.find((p: IShippingPolicy) => p.id === shippingPolicyId);

    if (!policy) {
      console.warn("Shipping policy not found for ID:", shippingPolicyId);
    }

    return policy ? policy.price : 0;
  }

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
  const itemTotals = useMemo(() => {
    const totals = (getBasket.data?.data.items ?? []).reduce(
      (acc, item) => {
        const inputPrice = getItemPrice(item);
        const grossTaxRate = Math.min(Math.max(Number(item.taxRate) || 0, 0), 100) / 100;

        // AddTaxToTotal=true mode: input is treated as net.
        acc.subtotalWithTaxAdded += inputPrice;
        acc.taxWithTaxAdded += inputPrice * grossTaxRate;

        // AddTaxToTotal=false mode: input is treated as gross and split into net+tax.
        acc.grossWithTaxIncluded += inputPrice;
        const netPrice = inputPrice / (1 + grossTaxRate);
        acc.subtotalWithTaxIncluded += netPrice;
        acc.taxWithTaxIncluded += inputPrice - netPrice;

        return acc;
      },
      {
        subtotalWithTaxAdded: 0,
        taxWithTaxAdded: 0,
        grossWithTaxIncluded: 0,
        subtotalWithTaxIncluded: 0,
        taxWithTaxIncluded: 0,
      }
    );

    return {
      subtotalWithTaxAdded: round2(totals.subtotalWithTaxAdded),
      taxWithTaxAdded: round2(totals.taxWithTaxAdded),
      grossWithTaxIncluded: round2(totals.grossWithTaxIncluded),
      subtotalWithTaxIncluded: round2(totals.subtotalWithTaxIncluded),
      taxWithTaxIncluded: round2(totals.taxWithTaxIncluded),
    };
  }, [getBasket.data?.data.items]);

  const itemSubtotal = addTaxToTotal
    ? itemTotals.subtotalWithTaxAdded
    : itemTotals.subtotalWithTaxIncluded;
  const itemTax = addTaxToTotal
    ? itemTotals.taxWithTaxAdded
    : itemTotals.taxWithTaxIncluded;
  const itemTotalBeforeDiscount = addTaxToTotal
    ? itemTotals.subtotalWithTaxAdded + itemTotals.taxWithTaxAdded
    : itemTotals.grossWithTaxIncluded;

  const effectiveTaxRate = itemSubtotal > 0 ? itemTax / itemSubtotal : 0;
  const discountBase = addTaxToTotal ? itemSubtotal : itemTotalBeforeDiscount;
  const requestedDiscount = discountType === DiscountType.Percentage
    ? discountBase * (Math.min(discountValue, 100) / 100)
    : discountValue;
  const appliedDiscount = Math.min(requestedDiscount, discountBase);

  const discountSubtotal = addTaxToTotal
    ? appliedDiscount
    : appliedDiscount / (1 + effectiveTaxRate);
  const discountTax = discountSubtotal * effectiveTaxRate;
  const discountTotal = discountSubtotal + discountTax;

  const basketSubtotal = round2(itemSubtotal - discountSubtotal);
  const basketTax = round2(itemTax - discountTax);

  const shippingPrice = getShippingPrice(watch("shippingPolicyId"));
  const finalTotal = round2(itemTotalBeforeDiscount - discountTotal + shippingPrice);

  if (isLoading) {
    return <FullscreenLoading />;
  }

  return (
    <>
      <ConfirmModal open={isConfirmOpen} setOpen={setIsConfirmOpen}
        title={"Are you sure you want sell these items?"}
        message={`Parts will be withdrawn from eBay and marked as sold and money received.`}
        confirmButtonText={"Submit"}
        onConfirm={handleSubmit}
        isLoading={isSubmitLoading}
      />
      <SaveAsQuoteModal
        open={isQuoteOpen}
        setOpen={setIsQuoteOpen}
        selectedAccount={selectedAccount}
        buildBody={buildQuoteBody}
        onSaved={() => {
          reset();
          setSelectedAccount(undefined);
        }}
      />
      <form className="flex flex-col w-full gap-4">
        {loadedQuote && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">
            <span>
              Loaded from quote for <span className="font-semibold">{loadedQuote.contactName || loadedQuote.accountName || "customer"}</span>. Submitting will mark the quote as converted.
            </span>
            <button type="button" className="text-xs font-medium underline" onClick={() => navigate(`/sales-quotes/${loadedQuote.id}`)}>View quote</button>
          </div>
        )}
        {isEmpty ? (
          <p className="mt-8 text-center text-xl text-gray-700">Your basket is empty</p>
        ) :
          <BasketTable data={getBasket.data?.data.items ?? []} />
        }

        <BasketInvoiceInputs
          register={register}
          setValue={setValue}
          watch={watch}
          setSelectedAccount={setSelectedAccount}
        />

        <div className="flex items-end justify-between gap-4">
          <div className="flex-1 rounded-md border border-gray-200 p-3 max-w-xs">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-gray-700">Discount</p>
              <p className="text-xs text-gray-500">Applied before shipping</p>
            </div>

            <div className="mt-2 flex items-end gap-2">
              <div className="flex-1">
                <TextInput
                  label={""}
                  register={register}
                  registerName={"discountValue"}
                  type="number"
                  customClasses="h-[38px]"
                />
              </div>
              <DiscountTypeToggleInput
                label=""
                classes="shrink-0"
                register={register}
                registerName={"discountType"}
                setValue={setValue}
                watch={watch}
              />
            </div>
          </div>

          <div className="shrink-0">
            <p className="text-right text-xl text-gray-700">Total: {getBasket.data?.data.items?.length} items</p>
            <p className="text-right text-xl text-gray-700">Subtotal: {PriceFormat(basketSubtotal)}</p>
            <p className="text-right text-xl text-gray-700">Total Tax: {PriceFormat(basketTax)}</p>
            {appliedDiscount > 0 && (
              <p className="text-right text-xl text-emerald-700">
                Discount ({discountType === DiscountType.Percentage ? `${Math.min(discountValue, 100)}%` : PriceFormat(discountValue)}): -{PriceFormat(round2(discountTotal))}
              </p>
            )}
            <p className="text-right text-xl text-gray-700">Shipping: {PriceFormat(shippingPrice)}</p>
            <p className="text-right text-2xl font-semibold text-gray-900">Total Price: {PriceFormat(finalTotal)}</p>
          </div>
        </div>

        <div className="flex justify-between mt-4">
          <ButtonInput label="Clear" classes={classNames(isEmpty ? "" : "!bg-red-600 hover:!bg-red-700")} onClick={() => clearBasket.mutate()} disabled={isEmpty} />
          <div className="flex gap-2">
            <ButtonInput label="Save as Quote" classes="!bg-white border border-gray-300 !text-gray-700 hover:!bg-gray-50" onClick={() => setIsQuoteOpen(true)} disabled={isEmpty} />
            {hasTill && (
              // Same basket, different screen: the till reads the user's pending basket, so this is a hand-off, not a copy.
              <ButtonInput label="Take payment at till" classes="!bg-white !text-primary-700 border !border-primary-300 hover:!bg-primary-50" onClick={() => navigate("/till")} disabled={isEmpty} />
            )}
            <ButtonInput label="Submit" classes={undefined} onClick={() => setIsConfirmOpen(true)} disabled={isEmpty} />
          </div>
        </div>
      </form>
    </>
  );
}